import { useState } from 'react';
import type { Dimension } from '../types';
import './ControlPanel.css';

interface ControlPanelProps {
  dimension: Dimension;
  onGenerate: (params: Record<string, unknown>) => void;
  isGenerating: boolean;
  sourceAudio: string | null;
  onFileUpload: (base64: string) => void;
}

export function ControlPanel({
  dimension,
  onGenerate,
  isGenerating,
  sourceAudio,
  onFileUpload,
}: ControlPanelProps) {
  // Generate params
  const [prompt, setPrompt] = useState('electronic, ambient, dreamy, ethereal');
  const [lyrics, setLyrics] = useState('');
  const [duration, setDuration] = useState(60);
  const [inferenceSteps, setInferenceSteps] = useState(60);
  const [guidanceScale, setGuidanceScale] = useState(15);
  const [seed, setSeed] = useState(-1);

  // Retake params
  const [variance, setVariance] = useState(0.5);

  // Repaint params
  const [startTime, setStartTime] = useState(0);
  const [endTime, setEndTime] = useState(10);

  // Edit params
  const [editMin, setEditMin] = useState(0);
  const [editMax, setEditMax] = useState(1);

  // Extend params
  const [leftSeconds, setLeftSeconds] = useState(0);
  const [rightSeconds, setRightSeconds] = useState(30);

  const convertToWav = async (file: File): Promise<string> => {
    const arrayBuffer = await file.arrayBuffer();
    const audioContext = new AudioContext();
    const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);

    // Convert to WAV
    const numChannels = audioBuffer.numberOfChannels;
    const sampleRate = audioBuffer.sampleRate;
    const length = audioBuffer.length * numChannels * 2;
    const buffer = new ArrayBuffer(44 + length);
    const view = new DataView(buffer);

    // WAV header
    const writeString = (offset: number, str: string) => {
      for (let i = 0; i < str.length; i++) {
        view.setUint8(offset + i, str.charCodeAt(i));
      }
    };

    writeString(0, 'RIFF');
    view.setUint32(4, 36 + length, true);
    writeString(8, 'WAVE');
    writeString(12, 'fmt ');
    view.setUint32(16, 16, true);
    view.setUint16(20, 1, true);
    view.setUint16(22, numChannels, true);
    view.setUint32(24, sampleRate, true);
    view.setUint32(28, sampleRate * numChannels * 2, true);
    view.setUint16(32, numChannels * 2, true);
    view.setUint16(34, 16, true);
    writeString(36, 'data');
    view.setUint32(40, length, true);

    // Write audio data
    const channels = [];
    for (let i = 0; i < numChannels; i++) {
      channels.push(audioBuffer.getChannelData(i));
    }

    let offset = 44;
    for (let i = 0; i < audioBuffer.length; i++) {
      for (let ch = 0; ch < numChannels; ch++) {
        const sample = Math.max(-1, Math.min(1, channels[ch][i]));
        view.setInt16(offset, sample < 0 ? sample * 0x8000 : sample * 0x7FFF, true);
        offset += 2;
      }
    }

    await audioContext.close();

    // Convert to base64
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate it's an audio file
    if (!file.type.startsWith('audio/')) {
      alert('Please upload an audio file (WAV, MP3, FLAC, OGG, M4A)');
      e.target.value = '';
      return;
    }

    try {
      const wavBase64 = await convertToWav(file);
      onFileUpload(wavBase64);
    } catch (err) {
      alert('Failed to process audio file. Please try a different format.');
      e.target.value = '';
    }
  };

  const handleSubmit = () => {
    switch (dimension) {
      case 'generate':
        onGenerate({
          prompt,
          lyrics,
          duration,
          inference_steps: inferenceSteps,
          guidance_scale: guidanceScale,
          seed,
        });
        break;
      case 'retake':
        if (!sourceAudio) return;
        onGenerate({
          source_audio_b64: sourceAudio,
          prompt,
          variance,
          seed,
        });
        break;
      case 'repaint':
        if (!sourceAudio) return;
        onGenerate({
          source_audio_b64: sourceAudio,
          start_time: startTime,
          end_time: endTime,
          prompt,
          lyrics,
        });
        break;
      case 'edit':
        if (!sourceAudio) return;
        onGenerate({
          source_audio_b64: sourceAudio,
          new_prompt: prompt,
          new_lyrics: lyrics,
          edit_n_min: editMin,
          edit_n_max: editMax,
        });
        break;
      case 'extend':
        if (!sourceAudio) return;
        onGenerate({
          source_audio_b64: sourceAudio,
          left_seconds: leftSeconds,
          right_seconds: rightSeconds,
          prompt,
        });
        break;
    }
  };

  const requiresSourceAudio = dimension !== 'generate';
  const canSubmit = !isGenerating && (!requiresSourceAudio || sourceAudio);

  return (
    <div className="control-panel">
      <h3 className="panel-title">
        {dimension.charAt(0).toUpperCase() + dimension.slice(1)} Settings
      </h3>

      {requiresSourceAudio && (
        <div className="form-group">
          <label>Source Audio</label>
          <div className="file-upload">
            <input
              type="file"
              accept=".wav,.mp3,.flac,.ogg,.m4a,.aac,audio/*"
              onChange={handleFileChange}
              id="audio-upload"
            />
            <label htmlFor="audio-upload" className="file-label">
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M9 16h6v-6h4l-7-7-7 7h4zm-4 2h14v2H5z" />
              </svg>
              {sourceAudio ? 'Audio loaded' : 'Upload audio file'}
            </label>
            <span className="file-hint">WAV, MP3, FLAC, OGG, M4A</span>
          </div>
        </div>
      )}

      {(dimension === 'generate' || dimension === 'retake' || dimension === 'repaint' || dimension === 'edit' || dimension === 'extend') && (
        <div className="form-group">
          <label>Prompt / Tags</label>
          <input
            type="text"
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            placeholder="electronic, ambient, dreamy..."
            className="text-input"
          />
        </div>
      )}

      {(dimension === 'generate' || dimension === 'repaint' || dimension === 'edit') && (
        <div className="form-group">
          <label>Lyrics</label>
          <textarea
            value={lyrics}
            onChange={(e) => setLyrics(e.target.value)}
            placeholder="[verse]&#10;Walking through the night..."
            className="text-input lyrics"
            rows={4}
          />
        </div>
      )}

      {dimension === 'generate' && (
        <>
          <div className="form-row">
            <div className="form-group">
              <label>Duration (s)</label>
              <input
                type="number"
                value={duration}
                onChange={(e) => setDuration(Number(e.target.value))}
                min={10}
                max={240}
                className="number-input"
              />
            </div>
            <div className="form-group">
              <label>Steps</label>
              <input
                type="number"
                value={inferenceSteps}
                onChange={(e) => setInferenceSteps(Number(e.target.value))}
                min={20}
                max={150}
                className="number-input"
              />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Guidance</label>
              <input
                type="number"
                value={guidanceScale}
                onChange={(e) => setGuidanceScale(Number(e.target.value))}
                min={1}
                max={30}
                step={0.5}
                className="number-input"
              />
            </div>
            <div className="form-group">
              <label>Seed</label>
              <input
                type="number"
                value={seed}
                onChange={(e) => setSeed(Number(e.target.value))}
                className="number-input"
                placeholder="-1 for random"
              />
            </div>
          </div>
        </>
      )}

      {dimension === 'retake' && (
        <div className="form-group">
          <label>Variance: {variance.toFixed(2)}</label>
          <input
            type="range"
            value={variance}
            onChange={(e) => setVariance(Number(e.target.value))}
            min={0}
            max={1}
            step={0.05}
            className="range-input"
          />
        </div>
      )}

      {dimension === 'repaint' && (
        <div className="form-row">
          <div className="form-group">
            <label>Start (s)</label>
            <input
              type="number"
              value={startTime}
              onChange={(e) => setStartTime(Number(e.target.value))}
              min={0}
              className="number-input"
            />
          </div>
          <div className="form-group">
            <label>End (s)</label>
            <input
              type="number"
              value={endTime}
              onChange={(e) => setEndTime(Number(e.target.value))}
              min={0}
              className="number-input"
            />
          </div>
        </div>
      )}

      {dimension === 'edit' && (
        <div className="form-row">
          <div className="form-group">
            <label>Edit Min</label>
            <input
              type="number"
              value={editMin}
              onChange={(e) => setEditMin(Number(e.target.value))}
              min={0}
              max={1}
              step={0.1}
              className="number-input"
            />
          </div>
          <div className="form-group">
            <label>Edit Max</label>
            <input
              type="number"
              value={editMax}
              onChange={(e) => setEditMax(Number(e.target.value))}
              min={0}
              max={1}
              step={0.1}
              className="number-input"
            />
          </div>
        </div>
      )}

      {dimension === 'extend' && (
        <div className="form-row">
          <div className="form-group">
            <label>Extend Left (s)</label>
            <input
              type="number"
              value={leftSeconds}
              onChange={(e) => setLeftSeconds(Number(e.target.value))}
              min={0}
              max={120}
              className="number-input"
            />
          </div>
          <div className="form-group">
            <label>Extend Right (s)</label>
            <input
              type="number"
              value={rightSeconds}
              onChange={(e) => setRightSeconds(Number(e.target.value))}
              min={0}
              max={120}
              className="number-input"
            />
          </div>
        </div>
      )}

      <button
        className={`generate-btn ${isGenerating ? 'generating' : ''}`}
        onClick={handleSubmit}
        disabled={!canSubmit}
      >
        {isGenerating ? (
          <>
            <span className="spinner" />
            Generating...
          </>
        ) : (
          <>
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 3v10.55c-.59-.34-1.27-.55-2-.55-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4V7h4V3h-6z" />
            </svg>
            {dimension.charAt(0).toUpperCase() + dimension.slice(1)}
          </>
        )}
      </button>
    </div>
  );
}







